// Tela de Cronograma de Coleta/Devolução (domínio Operacional): uma grade
// semanal fixa — segunda a domingo × 06:00 a 17:00 de 30 em 30 minutos — com
// as Coletas e Devoluções que as Paradas de cada hotel geram numa semana
// típica. Não navega entre semanas: a grade é sempre a mesma (ADR: ver
// `cronograma.ts` no domínio).

import {
  calcularIntervaloHorarios,
  DIA_SEMANA_LABEL,
  DIAS_DISPLAY,
  eventosCronograma,
  gradeCronograma,
  rotuloEventoCronograma,
  type TipoEventoCronograma,
} from "../../domain/cronograma.ts";
import type { Store } from "../store.ts";
import { clear, el, botao } from "../ui.ts";
import { botaoLimparFiltros, telaLista } from "./_cadastro.ts";
import {
  exportarCronograma,
  type FormatoExportCronograma,
} from "./_cronograma-export.ts";
import { multiSelect } from "./_multi-select.ts";

export function renderCronograma(
  container: HTMLElement,
  store: Store,
): () => void {
  // marca o `.content` da casca para a grade poder ocupar o espaço vertical
  // que sobrar na janela (ver `.content.cronograma-content` no styles.css) —
  // sem isso, `.content` só cresce até a altura do próprio conteúdo.
  container.classList.add("cronograma-content");

  let msClientePrev: string[] | null = null;
  const msTipo = multiSelect({
    rotulo: "Tipo",
    itens: [
      { id: "coleta", nome: "Coleta" },
      { id: "devolucao", nome: "Devolução" },
    ],
    todosPorPadrao: true,
    aoMudar: () => render(),
  });

  const msCliente = multiSelect({
    rotulo: "Cliente",
    itens: [],
    todosPorPadrao: true,
    aoMudar: () => { msClientePrev = msCliente.selecionados(); render(); },
  });

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const clientesAtivos = [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    msCliente.atualizarItens(clientesAtivos.map((c) => ({ id: c.id, nome: c.nome })));
    if (!msClientePrev) msCliente.marcarTodos();

    const filtroTipo = new Set(msTipo.selecionados());
    const filtroCliente = new Set(msCliente.selecionados());
    const todosEventos = eventosCronograma(snap);
    const { horaInicial, horaFinal } = calcularIntervaloHorarios(todosEventos);
    const eventos = todosEventos.filter(
      (e) =>
        (filtroTipo.size === 0 || filtroTipo.has(e.tipo)) &&
        (filtroCliente.size === 0 || filtroCliente.has(e.clienteId)),
    );
    const grade = gradeCronograma(eventos, horaInicial, horaFinal);

    const selFormato = el("select", {}) as HTMLSelectElement;
    for (const [v, r] of [
      ["pdf", "PDF"],
      ["jpeg", "Imagem (JPEG)"],
      ["csv", "Planilha (CSV)"],
    ] as const) {
      selFormato.append(el("option", { value: v }, r));
    }
    const exportar = botao(
      "primario",
      {},
      "Exportar",
    ) as HTMLButtonElement;
    exportar.addEventListener("click", () => {
      exportar.disabled = true;
      void exportarCronograma(
        selFormato.value as FormatoExportCronograma,
        grade,
        snap.config.rodapeExport,
      ).finally(() => {
        exportar.disabled = false;
      });
    });

    const barraExportar = el(
      "div",
      { class: "barra-filtros cronograma-export" },
      el(
        "div",
        { class: "barra-filtros-fim" },
        el("label", { class: "filtro" }, "Formato", selFormato),
        exportar,
      ),
    );

    const limparFiltros = botaoLimparFiltros(() => {
      msTipo.marcarTodos();
      msClientePrev = null;
      msCliente.marcarTodos();
      render();
    });

    const barraFiltros = el(
      "div",
      { class: "barra-filtros" },
      msTipo.element,
      msCliente.element,
      limparFiltros,
    );

    const colgroup = el(
      "colgroup",
      {},
      el("col", { style: "width: 8%" }),
      ...DIAS_DISPLAY.map(() => el("col", { style: `width: ${(92 / 7).toFixed(3)}%` })),
    );
    const cabecalho = el(
      "tr",
      {},
      el("th", { class: "cronograma-hcol" }, "Horário"),
      ...DIAS_DISPLAY.map((dia) => el("th", {}, DIA_SEMANA_LABEL[dia])),
    );
    const linhas = grade.linhas.map((linha) =>
      el(
        "tr",
        {},
        el("th", { scope: "row" }, linha.hora),
        ...linha.celulas.map((cel) =>
          el(
            "td",
            { class: "cronograma-cel" },
            ...cel.eventos.map((e) =>
              el(
                "span",
                {
                  class: `cronograma-chip cronograma-chip-${e.tipo === "coleta" ? "coleta" : "devolucao"}`,
                },
                rotuloEventoCronograma(e),
              ),
            ),
          ),
        ),
      ),
    );

    const tabela = el(
      "div",
      { class: "table-wrap cronograma-table-wrap" },
      el(
        "table",
        { class: "cronograma" },
        colgroup,
        el("thead", {}, cabecalho),
        el("tbody", {}, ...linhas),
      ),
    );

    const legenda = el(
      "p",
      { class: "cronograma-legenda" },
      el(
        "span",
        { class: "cronograma-legenda-item" },
        el("span", { class: "cronograma-legenda-dot coleta" }),
        "Coleta",
      ),
      el(
        "span",
        { class: "cronograma-legenda-item" },
        el("span", { class: "cronograma-legenda-dot devolucao" }),
        "Devolução",
      ),
    );

    const blocos: HTMLElement[] = [barraFiltros, tabela, legenda];
    if (grade.foraDoHorario.length > 0) {
      blocos.push(
        el(
          "p",
          { class: "muted" },
          `${grade.foraDoHorario.length} evento(s) fora do intervalo exibido (antes de ${String(horaInicial).padStart(2, "0")}:00 ou depois de ${String(horaFinal).padStart(2, "0")}:00):`,
        ),
        el(
          "ul",
          {},
          ...grade.foraDoHorario.map((e) =>
            el("li", { class: "muted" }, `${rotuloEventoCronograma(e)} — ${DIA_SEMANA_LABEL[e.dia]} ${e.hora}`),
          ),
        ),
      );
    }
    blocos.push(barraExportar);

    container.append(telaLista(...blocos));
  };

  render();
  const cancelar = store.subscribe(render);
  return () => {
    cancelar();
    container.classList.remove("cronograma-content");
  };
}
